import { Avatar, Popover, Row } from 'antd';
import clsx from 'clsx';
import AnyBodyImage from 'components/AnyBodyImage';
import Box from 'components/Box';
import Button from 'components/Button';
import Text from 'components/Text';
import paymentActions from 'features/payment/services/actions';
import userActions from 'features/user/services/actions';
import { IBillWaitingTicketResItem, IStaffTicketResItem } from 'features/user/services/types/ticket';
import { customerEditorRef } from 'features/user/widgets/WaitingList/CustomerEditor';
import {
  CustomerInfoPopoverUI,
  IconInfo,
  useCustomerInfoPopover,
} from 'features/user/widgets/WaitingList/WalkIn/components/CustomerName';
import { first } from 'lodash';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import storage from 'utils/sessionStorage';
import IcRevert from './IcRevert';
import { TickV2 } from 'assets/Icons/tick';

type ITicketItemProps = {
  data: IBillWaitingTicketResItem;
  viewOnly?: boolean;
  removeMode?: boolean;
  onRemoveTicket?: () => void;
  enableMergeTicket?: boolean;
  mergeTicketsList?: string[];
  onSelectMergeTicket?: () => void;
  cashier?: boolean;
  displayAvatarStaff?: boolean;
  openModalConfirmRevertToWaiting?: (id: string) => void;
  handleCallStaffs?: (staffs: IStaffTicketResItem[]) => void;
};
const TicketItem: React.FC<ITicketItemProps> = ({
  cashier,
  data,
  viewOnly,
  removeMode,
  onRemoveTicket = () => undefined,
  enableMergeTicket,
  mergeTicketsList = [],
  onSelectMergeTicket = () => undefined,
  openModalConfirmRevertToWaiting = () => undefined,
  handleCallStaffs = () => undefined,
  displayAvatarStaff,
}) => {
  const navigate = useNavigate();
  const enablePrintCheckIn = true;
  const dispatch = useAppDispatch();
  const onClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    if (enableMergeTicket) {
      e.preventDefault();
      onSelectMergeTicket();
      return;
    }
    if (viewOnly) return;
    if (!data) return;
    navigate(`/store/${storage.shop_id.get()}/ticket/edit/${data.billId}`);
  };

  const StaffAvatar = ({ onClick }: { onClick: (e: React.MouseEvent<HTMLDivElement>) => void }) => {
    const staff = first(data.staffs);
    if (!displayAvatarStaff) return (
      <Box display='flex' style={{ flex: 1, justifyContent: 'flex-end', alignSelf: 'stretch' }} onClick={onClick}>
        <StaffName>
          {data.staffs?.map((o) => o.staffName).join(', ') || 'Anybody'}
        </StaffName>
      </Box>
    );
    if (!staff) return <div onClick={onClick}><AnyBodyImage /></div>;
    return (
      <Box display='flex' alignItems='center' onClick={onClick}>
        {data.staffs.slice(0, 3)?.map((o, index) => (
          <div
            key={o.staffId}
            style={{
              position: 'relative',
              marginLeft: index === 0 ? 0 : -25,
              zIndex: 3 - index,
            }}
          >
            <Avatar
              size={55}
              src={o.avatar}
            >
              {o.staffName?.[0] || 'A'}
            </Avatar>

            {o?.finishTurn && (
              <BoxIcon>
                <TickV2 />
              </BoxIcon>
            )}
          </div>
        ))}
      </Box>
    );
  };

  const handlePrint = (e: any) => {
    e.stopPropagation();
    dispatch(
      // @ts-ignore
      paymentActions.getInfoPrintWaiting.fetch({
        body: JSON.stringify({ id: data.billId, type: 'PENDING_TICKET' }),
      })
    );
  };

  const handleClickRevertToWaiting = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    openModalConfirmRevertToWaiting?.(data.billId);
  };

  const handleClickStaffAvatar = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    handleCallStaffs?.(data.staffs);
  };
  if (!data) return null;
  return (
    <TicketItemStyled onClick={onClick} appointment={data.isAppointment}>
      <Box display='flex' style={{ gap: 4, alignItems: 'center' }}>
        {data.checkInId && (
          <RevertToWaitingBox onClick={handleClickRevertToWaiting}>
            <IcRevert />
          </RevertToWaitingBox>
        )}
        <TicketNumber
          variant='BODY_2'
          color='text_3'
          style={{ fontSize: cashier ? '2.2rem' : '1.9rem' }}
        >
          #{data.ticketNumber}
        </TicketNumber>
        {!!displayAvatarStaff && <span className='staff-name'>{data.staffs?.map((o) => o.staffName).join(', ')}</span>}
        <StaffAvatar onClick={handleClickStaffAvatar} />
      </Box>
      <CustomerBox id={data.customerId || ''} extraData={data} />
      <Box display='flex' alignItems='center'>
        <Box
          display='flex'
          flexDirection='column'
          alignItems='start'
          style={{ flex: 1 }}
          className='text-overflow'
        >
          <Text
            variant='BODY_2'
            color='text_3'
            className='ticket-price'
            style={cashier ? { fontSize: 18 } : undefined}
          >
            {formatCurrency(data.totalPrice)}
          </Text>
        </Box>
        <Box alignSelf='flex-end'>
          {enableMergeTicket ? (
            <Row
              align={'middle'}
              justify={'center'}
              className={clsx('circle-status', cashier && 'cashier')}
            >
              {mergeTicketsList.includes(data.billId) ? (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='17'
                  height='12'
                  viewBox='0 0 17 12'
                  fill='none'
                >
                  <path
                    d='M6.35192 11.7369C6.03176 11.7369 5.72761 11.6088 5.5035 11.3847L0.973172 6.85445C0.508943 6.39022 0.508943 5.62184 0.973172 5.15761C1.4374 4.69338 2.20578 4.69338 2.67001 5.15761L6.35192 8.83943L14.58 0.611356C15.0442 0.147126 15.8126 0.147126 16.2768 0.611356C16.7411 1.07559 16.7411 1.84397 16.2768 2.30819L7.20034 11.3847C6.97622 11.6088 6.67207 11.7369 6.35192 11.7369Z'
                    fill='#1D2129'
                  />
                </svg>
              ) : null}
            </Row>
          ) : (
            <>
              {removeMode ? (
                <Row justify={'center'} className='remove-row'>
                  <Button
                    icon='deleteIconCashier'
                    size='small'
                    height={2}
                    style={{ height: '2rem', padding: '0 4px' }}
                    onClick={(e) => [e.stopPropagation(), e.preventDefault(), onRemoveTicket()]}
                  />
                </Row>
              ) : enablePrintCheckIn ? (
                <Row justify={'center'}>
                  <Button
                    icon='print'
                    size='small'
                    height={2}
                    style={{ height: '2rem', padding: '4px' }}
                    onClick={handlePrint}
                  />
                </Row>
              ) : null}
            </>
          )}
        </Box>
      </Box>
    </TicketItemStyled>
  );
};

export default TicketItem;
type CustomerBoxProps = {
  id: string;
  extraData: IBillWaitingTicketResItem;
};
const CustomerBox = ({ id, extraData }: CustomerBoxProps) => {
  const dispatch = useAppDispatch();
  const {
    customerInfo,
    customerDetailLoading,
    viewCustomer,
    open,
    setOpen,
    handleCheckPassBeforeView,
    dataPin,
  } = useCustomerInfoPopover(id);

  const onOpenChange = (val: boolean) => handleCheckPassBeforeView(val);
  const onEdit = () => {
    customerEditorRef.current?.openWithId(customerInfo.customerId, {
      addon: customerInfo,
      onSuccess: (data) => {
        if (data) {
          dispatch(userActions.syncCustomerInfo(data));
        }
      },
    });
    setOpen(false);
  };

  return (
    <WrapperInfoBox>
      <Popover
        placement='rightTop'
        title={null}
        content={
          <div onClick={(e) => e.stopPropagation()}>
            <CustomerInfoPopoverUI
              loading={customerDetailLoading}
              customerInfo={customerInfo}
              onOpenChange={onOpenChange}
              onEdit={onEdit}
              dataPin={dataPin}
            />
          </div>
        }
        open={open}
        trigger={'click'}
        onOpenChange={onOpenChange}
        overlayInnerStyle={{ padding: 16, borderRadius: 8, background: '#E4F4F6' }}
      >
        <Box display='flex' alignItems='center' gap={'2'} style={{ flex: 1, overflow: 'hidden' }}>
          {!!id && (
            <button type='button' onClick={viewCustomer} style={{ display: 'flex' }}>
              <IconInfo />
            </button>
          )}
          <Text variant='H7' color='text_3' className='text-overflow' style={{ fontSize: '1rem' }}>
            {extraData.customerName || 'Unknown'}
          </Text>
          <div className='visited'>(Visited: {extraData.numberVisited || 0})</div>
        </Box>
      </Popover>
    </WrapperInfoBox>
  );
};
const WrapperInfoBox = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  position: relative;
  align-self: stretch;
  .visited {
    white-space: nowrap;
    font-weight: 700;
    font-size: 1rem;
    text-align: right;
  }
`;
type TicketItemStyledProps = {
  appointment?: boolean;
};
const TicketItemStyled = styled.button<TicketItemStyledProps>`
  height: 100%;
  width: 100%;
  border-radius: 5px;
  border: 1px solid #fdcc43;
  background: #feedbfab;
  padding: 3px;
  display: flex;
  flex-direction: column;

  .ticket-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.5rem;
  }
  .ticket-body {
    text-align: center;
  }
  .remove-row {
    svg {
      width: 24px;
      height: 24px;
    }
  }

  ${({ appointment }) =>
    appointment &&
    `
    background: #C6E9EF;
    border: 1px solid #6FABB6;
  `}

  .circle-status {
    background: #fff;
    border-radius: 4px;
    height: 32px;
    width: 32px;
    border: 1px solid #c9cdd4;
    &.cashier {
      height: 48px;
      width: 48px;
      svg {
        transform: scale(1.5);
      }
    }
  }
  .staff-name {
    font-weight: 700;
    text-transform: uppercase;
    font-size: 1rem;
    text-align: left;
    color: red;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: right;
    flex: 1;
  }
  .ticket-price {
    font-size: 16px;
  }
`;
const TicketNumber = styled(Text)`
  display: inline-flex;
  padding: 2px 4px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 5px;
  background: #1d2129;
  color: white;
`;

const RevertToWaitingBox = styled.div`
  width: 34px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BoxIcon = styled.div`
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
`;

const StaffName = styled.span`
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.9rem;
  text-align: right;
  color: red;
  width: 85%;
`;